import { zodResolver } from '@hookform/resolvers/zod';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import type { PhaseId as ApiPhaseId, UpdateProjectCommand } from '@/api/generated';
import { useUpdateProject } from '@/api/hooks';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Input } from '@/design-system/primitives/Input/Input';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { PHASES } from '@/domain/phases';
import styles from './Onboarding.module.css';

// Step order on the wizard. We persist the form values in sessionStorage so
// a refresh mid-wizard doesn't wipe progress; the API mutation only fires on
// the final step.
const STEPS = ['project', 'adres', 'planning', 'bevestig'] as const;
type StepKey = (typeof STEPS)[number];

const TYPES: Array<{ id: string; title: string; sub: string }> = [
  { id: 'nieuwbouw', title: 'Nieuwbouw', sub: 'sleutel-op-de-deur of in eigen beheer' },
  { id: 'renovatie', title: 'Renovatie', sub: 'bestaande woning ≥10 jaar' },
  { id: 'verbouwing', title: 'Verbouwing', sub: 'met uitbreiding' },
  { id: 'andere', title: 'Andere', sub: '' },
];

const DEADLINES: Array<{
  icon: 'calendar' | 'shield' | 'clock' | 'tool';
  title: string;
  sub: string;
}> = [
  {
    icon: 'calendar',
    title: 'Omgevingsvergunning',
    sub: 'Vervalt 18/03/2027 als niet gestart',
  },
  { icon: 'shield', title: 'EPB-aangifte', sub: 'Binnen 12 maand na ingebruikname' },
  { icon: 'clock', title: '10-jarige aansprakelijkheid', sub: 'Loopt vanaf voorlopige oplevering' },
  { icon: 'tool', title: 'Elektrische keuring', sub: 'Verplicht voor ingebruikname' },
];

// BF-11 — `projectType` + `currentPhaseId` now persist via UpdateProjectCommand.
// Schema unchanged; the on-submit handler maps the lowercase local PhaseId to
// the backend's TitleCase enum.
const schema = z.object({
  name: z.string().trim().min(2, 'Minstens 2 tekens'),
  projectType: z.enum(['nieuwbouw', 'renovatie', 'verbouwing', 'andere']),
  address: z.string().trim().max(200, 'Maximaal 200 tekens').optional().or(z.literal('')),
  startDate: z.string().min(1, 'Kies een startdatum'),
  endDate: z.string().min(1, 'Kies een einddatum'),
  phaseId: z.string().min(1, 'Kies een fase'),
  budgetTotal: z
    .string()
    .min(1, 'Verplicht veld')
    .refine((v) => /^\d+([.,]\d{1,2})?$/.test(v.trim()), 'Bedrag in euro (bv. 520000)'),
});

type OnboardingValues = z.infer<typeof schema>;

const STORAGE_KEY = 'bouw.onboarding';
const STEP_STORAGE_KEY = 'bouw.onboarding.step';

// Local PhaseId is lowercase (`ruw`); backend enum is TitleCase (`Ruw`).
// Wizard surfaces the local domain values to the chip group, so we translate
// at the boundary instead of plumbing two enums through the form.
const API_PHASE_BY_LOCAL: Record<string, ApiPhaseId> = {
  voor: 'Voor',
  ontw: 'Ontw',
  verg: 'Verg',
  aanb: 'Aanb',
  ruw: 'Ruw',
  wwd: 'Wwd',
  tech: 'Tech',
  afw: 'Afw',
  opl: 'Opl',
  naz: 'Naz',
};

function toApiPhaseId(local: string | undefined): ApiPhaseId | null {
  if (!local) return null;
  return API_PHASE_BY_LOCAL[local] ?? null;
}

function readStored(): Partial<OnboardingValues> {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<OnboardingValues>) : {};
  } catch {
    return {};
  }
}

function writeStored(values: OnboardingValues): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // sessionStorage may be unavailable (private mode / strict quota) —
    // the wizard still works, just without refresh-resilience.
  }
}

function readStoredStep(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  try {
    const raw = sessionStorage.getItem(STEP_STORAGE_KEY);
    if (raw === null) return 0;
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.min(STEPS.length - 1, parsed));
  } catch {
    return 0;
  }
}

function writeStoredStep(step: number): void {
  try {
    sessionStorage.setItem(STEP_STORAGE_KEY, String(step));
  } catch {
    // no-op
  }
}

function clearStored(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STEP_STORAGE_KEY);
  } catch {
    // no-op
  }
}

const STEP_FIELDS: Record<StepKey, Array<keyof OnboardingValues>> = {
  project: ['name', 'projectType'],
  adres: ['address'],
  planning: ['startDate', 'endDate', 'phaseId', 'budgetTotal'],
  bevestig: [],
};

const STEP_TITLE: Record<StepKey, string> = {
  project: 'Vertel ons over je bouw.',
  adres: 'Waar bouw je?',
  planning: 'Wanneer en in welke fase?',
  bevestig: 'Klaar om te starten?',
};

const STEP_INTRO: Record<StepKey, string> = {
  project: 'Naam en type — niets is definitief, je past dit later aan in instellingen.',
  adres: 'Adres is optioneel, maar helpt om gemeente-specifieke vergunningen te bewaken.',
  planning: 'We gebruiken deze om de tijdlijn op te bouwen en de wettelijke deadlines te plannen.',
  bevestig: 'Klik op "Aan de slag" om je antwoorden op te slaan en het dashboard te openen.',
};

export function Onboarding() {
  const navigate = useNavigate();
  const updateProject = useUpdateProject();
  const [stepIndex, setStepIndex] = useState<number>(() => readStoredStep());
  const step = STEPS[stepIndex];

  const stored = readStored();
  const form = useForm<OnboardingValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zod 4 + RHF resolver typing mismatch.
    resolver: zodResolver(schema as any) as never,
    mode: 'onTouched',
    defaultValues: {
      name: stored.name ?? '',
      projectType: (stored.projectType as OnboardingValues['projectType']) ?? 'nieuwbouw',
      address: stored.address ?? '',
      startDate: stored.startDate ?? '',
      endDate: stored.endDate ?? '',
      phaseId: stored.phaseId ?? 'voor',
      budgetTotal: stored.budgetTotal ?? '',
    },
  });

  // Persist form values on every change so a refresh mid-wizard resumes where
  // we left off.
  useEffect(() => {
    const sub = form.watch((values) => writeStored(values as OnboardingValues));
    return () => sub.unsubscribe();
  }, [form]);

  // Persist the active step so a refresh resumes on the same screen.
  useEffect(() => {
    writeStoredStep(stepIndex);
  }, [stepIndex]);

  const next = async () => {
    const valid = await form.trigger(STEP_FIELDS[step]);
    if (!valid) return;
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
  };

  const back = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    const body: UpdateProjectCommand = {
      name: values.name.trim(),
      address: values.address && values.address.trim().length > 0 ? values.address.trim() : null,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      budgetTotal: values.budgetTotal.replace(',', '.'),
      currentPhaseId: toApiPhaseId(values.phaseId),
      projectType: values.projectType,
      taskNotificationDefaultOffsetsDays: null,
      legalNotificationContactId: null,
    };
    await updateProject.mutateAsync(body);
    clearStored();
    navigate('/');
  });

  const values = form.watch();

  // The form element is here purely to host RHF's field refs and to give
  // the inputs a semantic container. Submission is driven exclusively by the
  // "Aan de slag" button's onClick — we prevent default on the form's
  // onSubmit so a stray Enter keypress on an intermediate step doesn't
  // accidentally finalise the wizard with half-filled data.
  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'bevestig') {
      void onSubmit(e);
    }
  };

  return (
    <div className={`bv ${styles.root}`}>
      <form className={styles.left} onSubmit={onFormSubmit} noValidate>
        <div className={`row gap-10 ${styles.brand}`}>
          <div className={styles.logo}>b</div>
          <div className={styles.brandText}>Bouw</div>
        </div>

        <div className={`eyebrow ${styles.step}`}>
          Stap {stepIndex + 1} van {STEPS.length} · {step}
        </div>
        <h1 className={styles.title}>{STEP_TITLE[step]}</h1>
        <p className={styles.intro}>{STEP_INTRO[step]}</p>

        <div className={`col gap-20 ${styles.form}`}>
          {step === 'project' && (
            <>
              <div className={`col gap-6 ${styles.field}`}>
                <label htmlFor="ob-name" className={styles.fieldLabel}>
                  Naam van het project
                </label>
                <Input
                  id="ob-name"
                  className={styles.bigInput}
                  placeholder="bv. 'Bertem' of 'Onze nieuwbouw'"
                  autoFocus
                  {...form.register('name')}
                />
                {form.formState.errors.name && (
                  <div role="alert" className={styles.errorMsg}>
                    {form.formState.errors.name.message}
                  </div>
                )}
              </div>

              <fieldset className={`col gap-6 ${styles.field}`}>
                <legend className={styles.fieldLabel}>Type project</legend>
                <div className={`row gap-8 ${styles.typeRow}`} role="radiogroup">
                  {TYPES.map((t) => {
                    const checked = values.projectType === t.id;
                    return (
                      <label
                        key={t.id}
                        className={`${styles.typeCard} ${checked ? styles.typeCardActive : ''}`}
                      >
                        <input
                          type="radio"
                          value={t.id}
                          className={styles.srOnly}
                          checked={checked}
                          onChange={() =>
                            form.setValue('projectType', t.id as OnboardingValues['projectType'], {
                              shouldDirty: true,
                            })
                          }
                        />
                        <div className={styles.typeTitle}>{t.title}</div>
                        <div className={styles.typeSub}>{t.sub}</div>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </>
          )}

          {step === 'adres' && (
            <div className={`col gap-6 ${styles.field}`}>
              <label htmlFor="ob-address" className={styles.fieldLabel}>
                Adres
              </label>
              <div className={styles.fieldHint}>
                Optioneel — helpt om gemeente-specifieke vergunningen te bewaken.
              </div>
              <Input
                id="ob-address"
                placeholder="bv. Kerkstraat 47, 3060 Bertem"
                {...form.register('address')}
              />
            </div>
          )}

          {step === 'planning' && (
            <>
              <div className={`row gap-16 ${styles.dateRow}`}>
                <div className={`col gap-6 ${styles.field}`}>
                  <label htmlFor="ob-start" className={styles.fieldLabel}>
                    Geschatte startdatum
                  </label>
                  <Input id="ob-start" type="date" {...form.register('startDate')} />
                  {form.formState.errors.startDate && (
                    <div role="alert" className={styles.errorMsg}>
                      {form.formState.errors.startDate.message}
                    </div>
                  )}
                </div>
                <div className={`col gap-6 ${styles.field}`}>
                  <label htmlFor="ob-end" className={styles.fieldLabel}>
                    Geschatte oplevering
                  </label>
                  <Input id="ob-end" type="date" {...form.register('endDate')} />
                  {form.formState.errors.endDate && (
                    <div role="alert" className={styles.errorMsg}>
                      {form.formState.errors.endDate.message}
                    </div>
                  )}
                </div>
              </div>

              <fieldset className={`col gap-6 ${styles.field}`}>
                <legend className={styles.fieldLabel}>In welke fase zit je nu?</legend>
                <div className={styles.fieldHint}>
                  We tonen alleen relevante schermen tot je verder bent.
                </div>
                <div className={styles.phaseChips} role="radiogroup">
                  {PHASES.map((p) => {
                    const checked = values.phaseId === p.id;
                    return (
                      <label
                        key={p.id}
                        className={`${styles.phaseChip} ${checked ? styles.phaseChipActive : ''}`}
                      >
                        <input
                          type="radio"
                          value={p.id}
                          className={styles.srOnly}
                          checked={checked}
                          onChange={() => form.setValue('phaseId', p.id, { shouldDirty: true })}
                        />
                        {p.label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <div className={`col gap-6 ${styles.field}`}>
                <label htmlFor="ob-budget" className={styles.fieldLabel}>
                  Budget (richtwaarde)
                </label>
                <div className={styles.fieldHint}>
                  Alleen voor de balk — exacte cijfers vul je later in.
                </div>
                <Input
                  id="ob-budget"
                  inputMode="decimal"
                  placeholder="520000"
                  className={`mono ${styles.budgetInput}`}
                  {...form.register('budgetTotal')}
                />
                {form.formState.errors.budgetTotal && (
                  <div role="alert" className={styles.errorMsg}>
                    {form.formState.errors.budgetTotal.message}
                  </div>
                )}
              </div>
            </>
          )}

          {step === 'bevestig' && (
            <>
              <Card className={styles.summaryCard}>
                <h3 className={styles.summaryTitle}>Samenvatting</h3>
                <dl className={styles.summary}>
                  <SummaryRow label="Project" value={values.name || '—'} />
                  <SummaryRow
                    label="Type"
                    value={TYPES.find((t) => t.id === values.projectType)?.title ?? '—'}
                  />
                  <SummaryRow label="Adres" value={values.address || '—'} />
                  <SummaryRow
                    label="Tijdlijn"
                    value={
                      values.startDate || values.endDate
                        ? `${values.startDate || '?'} → ${values.endDate || '?'}`
                        : '—'
                    }
                  />
                  <SummaryRow
                    label="Huidige fase"
                    value={PHASES.find((p) => p.id === values.phaseId)?.label ?? '—'}
                  />
                  <SummaryRow
                    label="Budget"
                    value={values.budgetTotal ? `€${values.budgetTotal}` : '—'}
                  />
                </dl>
              </Card>

              <Card className={styles.partnerCard}>
                <div className={`row gap-8 ${styles.partnerHead}`}>
                  <Avatar name="Bram Peeters" size={36} hue={210} />
                  <div className="flex-1">
                    <div className={styles.partnerName}>Partner of mede-bouwheer</div>
                    <div className={`mono ${styles.partnerEmail}`}>
                      Optioneel — kan ook later in instellingen.
                    </div>
                  </div>
                  <Pill kind="idle" dot={false}>
                    later
                  </Pill>
                </div>
              </Card>
            </>
          )}
        </div>

        <div className={`row gap-12 ${styles.footer}`}>
          <Btn
            type="button"
            variant="ghost"
            onClick={back}
            disabled={stepIndex === 0 || updateProject.isPending}
          >
            <Icon name="chevL" size={14} /> Terug
          </Btn>
          <div className={`ml-auto row gap-12 ${styles.dots}`}>
            {STEPS.map((s, i) => (
              <span
                key={s}
                className={i <= stepIndex ? styles.dotActive : styles.dot}
                aria-hidden="true"
              />
            ))}
          </div>
          {step === 'bevestig' ? (
            <Btn
              key="ob-submit"
              type="button"
              variant="primary"
              onClick={(e) => onSubmit(e)}
              disabled={updateProject.isPending}
            >
              {updateProject.isPending ? 'Bezig…' : 'Aan de slag'} <Icon name="chevR" size={14} />
            </Btn>
          ) : (
            <Btn key="ob-next" type="button" variant="primary" onClick={next}>
              Volgende stap <Icon name="chevR" size={14} />
            </Btn>
          )}
        </div>
      </form>

      <aside className={styles.right}>
        <div className={`eyebrow ${styles.previewEyebrow}`}>
          Voorbeeld op basis van je antwoorden
        </div>
        <h2 className={styles.previewTitle}>Zo ziet je tijdlijn er straks uit</h2>

        <Card className={styles.preview}>
          <div className={`row ${styles.previewHead}`}>
            <div className={styles.previewName}>{values.name || 'Nieuwbouw'}</div>
            <Pill kind="warn" dot={false}>
              fase · {PHASES.find((p) => p.id === values.phaseId)?.short ?? '—'}
            </Pill>
          </div>
          <div className={styles.previewDates}>
            {values.startDate || '—'} → {values.endDate || '—'}
          </div>

          <div className={styles.mini}>
            <div className={styles.miniRail} />
            <div className={styles.miniDone} />
            <div className={`row ${styles.miniRow}`}>
              {PHASES.map((p) => {
                const done = p.status === 'done';
                const doing = p.id === values.phaseId;
                return (
                  <div key={p.id} className={`col ${styles.miniCell}`}>
                    <div
                      className={`${styles.miniDot} ${
                        done ? styles.miniDotDone : doing ? styles.miniDotDoing : ''
                      }`}
                    />
                    <div className={`${styles.miniLabel} ${doing ? styles.miniLabelDoing : ''}`}>
                      {p.short}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <hr className={styles.previewDivider} />
          <div className={`eyebrow ${styles.deadlinesLabel}`}>
            Wettelijke deadlines die we bewaken
          </div>
          <div className="col gap-8">
            {DEADLINES.map((d) => (
              <div key={d.title} className="row gap-10">
                <div className={styles.deadlineIcon}>
                  <Icon name={d.icon} size={13} color="var(--ink-2)" />
                </div>
                <div className="flex-1">
                  <div className={styles.deadlineTitle}>{d.title}</div>
                  <div className={styles.deadlineSub}>{d.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <p className={styles.previewFoot}>
          Alles wat je hier invult kan je in de instellingen aanpassen.
        </p>
      </aside>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={`row gap-8 ${styles.summaryRow}`}>
      <dt className={styles.summaryKey}>{label}</dt>
      <dd className={styles.summaryValue}>{value}</dd>
    </div>
  );
}
